import { DatePipe } from '@angular/common';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { RichmediaAdsService } from 'src/app/core/services/richmedia-ads.service';
import { RichmediaItem, RootRichmedia, Article } from 'src/app/models/richmedia-ads';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-form-richmedia-ads',
  templateUrl: './form-richmedia-ads.component.html',
  styleUrls: ['./form-richmedia-ads.component.css']
})
export class FormRichmediaAdsComponent {
  formsAd!: FormGroup;
  dropdownOpenSections = false;
  dropdownOpenSectionsOut = false;
  secciones: { key: string; name: string }[] = [];
  selectedSection: string = '';
  isSubmitting = false;
  editIndex: number | null = null; //Para saber si es edición

  @ViewChild('artInput') artInput!: ElementRef<HTMLInputElement>;
  @ViewChild('artInputOut') artInputOut!: ElementRef<HTMLInputElement>;

  constructor(private fb: FormBuilder, private richmediaService: RichmediaAdsService, private router: Router, private route: ActivatedRoute,  private datePipe: DatePipe) { }

  ngOnInit(): void {
    this.formsAd = this.fb.group({
      name: ['', Validators.required],
      type: ['', Validators.required],
      activate_in: ['', Validators.required],
      sections: [[], Validators.required],
      sectionsOut: [[]],
      articles: [[]],
      articlesOut: [[]],
      device: ['', Validators.required],
      color: [''],
      start: ['', Validators.required],
      end: ['', Validators.required],
      plus: [0],
    });

    this.loadSections();

    this.route.paramMap.subscribe(async params => {
      const index = params.get('index');
      if (index !== null) {
        this.editIndex = +index;
        const root = await this.richmediaService.getRichmediaRoot();
        const item = root.richmedia[this.editIndex];
        if (item) {
          // Normalizamos articles y articlesOut
          const normalizedArticles = (item.articles ?? []).map((a: any) =>
            typeof a === 'string' ? { text: a } : a
          );

          const normalizedArticlesOut = (item.articlesOut ?? []).map((a: any) =>
            typeof a === 'string' ? { text: a } : a
          );

          // Adaptamos las fechas para que el input las entienda
          const adaptedItem = {
            ...item,
            articles: normalizedArticles,
            articlesOut: normalizedArticlesOut,
            start: this.datePipe.transform(item.start, 'yyyy-MM-ddTHH:mm'),
            end: this.datePipe.transform(item.end, 'yyyy-MM-ddTHH:mm'),
          };
          this.formsAd.patchValue(adaptedItem);
        }
      }
    });
  }

  loadSections(): void {
    this.richmediaService.getMenuSections()
      .then(sections => {
        // Agregamos "home" al inicio y all
        this.secciones = [
          { key: 'home', name: 'Home' },
          { key: 'all', name: 'Todos' },
          ...sections.sort((a, b) => a.name.localeCompare(b.name)) // ordena alfabéticamente
        ];

        // Si aún no hay sección seleccionada, dejamos "home"
        if (!this.selectedSection) {
          this.selectedSection = 'home';
        }

        //Forzar que sections arranque vacío
        this.formsAd.get('sections')?.setValue([]);
        this.formsAd.get('sectionsOut')?.setValue([]);

      })
      .catch(error => {
        console.error('Error al cargar secciones:', error);
      });
  }

  // --- SECCIONES ---
  get selectedSections(): string[] {
    return (this.formsAd.get('sections')?.value as string[]) ?? [];
  }

  isSelected(id: string): boolean {
    return this.selectedSections.includes(id);
  }

  toggleSeccion(id: string): void {
    const current = this.selectedSections;
    const next = this.isSelected(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    this.formsAd.get('sections')?.setValue(next);
    this.formsAd.get('sections')?.markAsDirty();

    // cerrar el dropdown después de seleccionar
    this.dropdownOpenSections = false;
  }

  addSeccion(event: KeyboardEvent): void {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();
    if (!value) return;

    const current = this.formsAd.get('sections')?.value as string[];
    if (!current.includes(value)) {
      this.formsAd.get('sections')?.setValue([...current, value]);
      this.formsAd.get('sections')?.markAsDirty();
    }

    input.value = '';
  }

  removeSeccion(index: number): void {
    const current = this.formsAd.get('sections')?.value as string[];
    current.splice(index, 1);
    this.formsAd.get('sections')?.setValue([...current]);
    this.formsAd.get('sections')?.markAsDirty();
  }

  getSeccionNombre(id: string): string {
    const found = this.secciones.find((s) => s.key === id);
    return found ? found.name : '';
  }


  trackBySeccion(index: number, item: { key: string; name: string }): string {
    return item.key;
  }

  // --- SECCIONES OUT ---
  get selectedSectionsOut(): string[] {
    return (this.formsAd.get('sectionsOut')?.value as string[]) ?? [];
  }

  isSelectedOut(id: string): boolean {
    return this.selectedSectionsOut.includes(id);
  }

  toggleSeccionOut(id: string): void {
    const current = this.selectedSectionsOut;
    const next = this.isSelectedOut(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    this.formsAd.get('sectionsOut')?.setValue(next);
    this.formsAd.get('sectionsOut')?.markAsDirty();
    this.dropdownOpenSectionsOut = false;
  }

  removeSeccionOut(id: string): void {
    const current = this.selectedSectionsOut;
    this.formsAd.get('sectionsOut')?.setValue(current.filter((x) => x !== id));
    this.formsAd.get('sectionsOut')?.markAsDirty();
  }

  // --- ARTÍCULOS ---
  get articles(): Article[] {
    return (this.formsAd.get('articles')?.value as Article[]) ?? [];
  }

  removeArticulo(index: number): void {
    const current = this.formsAd.get('articles')?.value as Article[];
    current.splice(index, 1);
    this.formsAd.get('articles')?.setValue([...current]);
    this.formsAd.get('articles')?.markAsDirty();
  }

  onArticuloKeyDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this._addArticulo(input.value, 'articles')) {
        input.value = '';
      }
    }
  }

  onArtPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const paste = event.clipboardData?.getData('text') ?? '';
    const parts = paste.split(/[\s,;]+/).map(p => p.trim()).filter(Boolean);

    for (const p of parts) {
      this._addArticulo(p, 'articles');
    }
    if (this.artInput?.nativeElement) this.artInput.nativeElement.value = '';
  }

  focusArtInput(): void {
    if (this.artInput?.nativeElement) {
      this.artInput.nativeElement.focus();
    }
  }

  // --- ARTÍCULOS OUT ---
  get articlesOut(): Article[] {
    return (this.formsAd.get('articlesOut')?.value as Article[]) ?? [];
  }

  removeArticuloOut(index: number): void {
    const current = this.formsAd.get('articlesOut')?.value as Article[];
    current.splice(index, 1);
    this.formsAd.get('articlesOut')?.setValue([...current]);
    this.formsAd.get('articlesOut')?.markAsDirty();
  }

  onArticuloOutKeyDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this._addArticulo(input.value, 'articlesOut')) {
        input.value = '';
      }
    }
  }

  onArtOutPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const paste = event.clipboardData?.getData('text') ?? '';
    const parts = paste.split(/[\s,;]+/).map(p => p.trim()).filter(Boolean);

    for (const p of parts) {
      this._addArticulo(p, 'articlesOut');
    }
    if (this.artInputOut?.nativeElement) this.artInputOut.nativeElement.value = '';
  }

  focusArtInputOut(): void {
    if (this.artInputOut?.nativeElement) {
      this.artInputOut.nativeElement.focus();
    }
  }

  // --- Artículos Reutilizables ---
  private _addArticulo(value: string, controlName: 'articles' | 'articlesOut'): boolean {
    const v = value.trim().toUpperCase();
    if (!v) return false;

    const regex = /^PP[0-9A-Z]+$/;
    if (!regex.test(v)) {
      Swal.fire('Error', 'Formato inválido. Ej: PP123456A', 'error');
      return false;
    }

    const current = this.formsAd.get(controlName)?.value as Article[];

    if (current.some(a => a.text === v)) {
      Swal.fire('Error', 'Este artículo ya fue agregado', 'error');
      return false;
    }

    this.formsAd.get(controlName)?.setValue([...current, { text: v }]); 
    this.formsAd.get(controlName)?.markAsDirty();
    return true;
  }
  
  async submitForm() {
    if (this.formsAd.invalid) {
      Swal.fire('', 'Falta información, complete los campos', 'warning');
      return;
    }

    // Adaptamos antes de enviar al backend
    const rawValue = this.formsAd.value;

    // Validación: end >= start
    const startDate = new Date(rawValue.start);
    const endDate = new Date(rawValue.end);

    if (endDate < startDate) {
      Swal.fire('Fecha inválida', 'La fecha de finalización no puede ser anterior a la fecha de inicio.', 'warning');
      return;
    }

    if (endDate.getTime() === startDate.getTime()) {
      await Swal.fire('Atención', 'La fecha de inicio y la de finalización son iguales.', 'warning');
      return;
    }

    //  Normalizamos los artículos a objetos { text: string }
    const normalizedArticles = (rawValue.articles ?? []).map((a: any) =>
      typeof a === 'string' ? { text: a } : a
    );

    const normalizedArticlesOut = (rawValue.articlesOut ?? []).map((a: any) =>
      typeof a === 'string' ? { text: a } : a
    );

    const newItem: RichmediaItem = {
      ...rawValue,
      articles: normalizedArticles,
      articlesOut: normalizedArticlesOut,
      start: this.datePipe.transform(rawValue.start, 'yyyy-MM-dd HH:mm:ss')!,
      end: this.datePipe.transform(rawValue.end, 'yyyy-MM-dd HH:mm:ss')!,
    };

    try {
      const currentRoot: RootRichmedia = await this.richmediaService.getRichmediaRoot();
      let updatedRoot: RootRichmedia = {
        breakingNews: currentRoot.breakingNews ?? null,
        richmedia: []
      };

      if (this.editIndex !== null) {
        const updatedList = [...currentRoot.richmedia];
        // updatedList[this.editIndex] = newItem;
        // updatedRoot.richmedia = updatedList;

        // eliminamos el elemento anterior en su posición original
        updatedList.splice(this.editIndex, 1);

        // insertamos el nuevo al inicio
        updatedRoot.richmedia = [newItem, ...updatedList];
      } else {
        updatedRoot.richmedia = [newItem, ...currentRoot.richmedia];
        //updatedRoot.richmedia = [...currentRoot.richmedia, newItem];
      }

      this.richmediaService.saveRichmedia(updatedRoot).subscribe({
        next: () => {
          Swal.fire(
            '',
            this.editIndex !== null ? 'Campaña actualizada con éxito' : 'Campaña creada con éxito',
            'success'
          ).then(() => this.router.navigate(['/home-richmedia']));
        },
        error: (err) => {
          console.error('Error al guardar', err);
          Swal.fire('Error', 'No se pudo guardar la campaña', 'error');
        }
      });

    } catch (err) {
      console.error('Error al preparar payload', err);
      Swal.fire('Error', 'No se pudo cargar el listado actual.', 'error');
    }
  }
  
}
